import { Worksheet } from 'exceljs';

// Upper half (0x80–0xFF) of the Mac OS Roman character set, in byte order.
const MAC_ROMAN_HIGH_CHARS =
  'ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø' +
  '¿¡¬√ƒ≈∆«»…\u00A0ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄€‹›ﬁﬂ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ\uF8FFÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ';

// Like Excel's "General" number format with German settings: at most 15 significant digits,
// no trailing zeros and a decimal comma.
const formatNumber = (value: number) => String(Number(value.toPrecision(15))).replace('.', ',');

const formatText = (value: string) =>
  /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * Writes the worksheet as CSV for the FiBu import (DOK-20): semicolon separated, as the
 * accounting software expects, decimal comma, quotes only where needed, CRLF between the rows
 * and no line break after the last one.
 */
export const worksheetToCsv = (worksheet: Worksheet): string => {
  const lines: string[] = [];
  for (let rowNumber = 1; rowNumber <= worksheet.rowCount; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    const fields: string[] = [];
    for (let columnNumber = 1; columnNumber <= worksheet.columnCount; columnNumber += 1) {
      const cell = row.getCell(columnNumber);
      fields.push(
        typeof cell.value === 'number' ? formatNumber(cell.value) : formatText(cell.text),
      );
    }
    lines.push(fields.join(';'));
  }
  return lines.join('\r\n');
};

/**
 * Encodes the text in Mac OS Roman, the character set of the CSV files saved by Excel for Mac.
 * Characters that Mac OS Roman doesn't have become '?'.
 */
export const encodeMacRoman = (text: string): Uint8Array => {
  const chars = Array.from(text);
  const bytes = new Uint8Array(chars.length);
  chars.forEach((char, index) => {
    const code = char.charCodeAt(0);
    const highIndex = MAC_ROMAN_HIGH_CHARS.indexOf(char);
    bytes[index] = code < 0x80 ? code : highIndex >= 0 ? 0x80 + highIndex : 0x3f;
  });
  return bytes;
};
